import React from 'react';
import axios from 'axios';
import { useGlobalModal } from '../contexts/GlobalModalContext';
import { useLanguage } from '../contexts/LanguageContext';
import {
  popoverButtonContainerStyle,
  getCancelButtonStyle,
  getSubmitButtonStyle
} from './shared/PopoverStyles';

/**
 * Global MarketplaceModal Component
 * Rendered once at App level, controlled by GlobalModalContext
 * Opens on any page without navigation
 */
const MarketplaceModal = () => {
  const { t } = useLanguage();
  const {
    // State
    showMarketplaceModal,
    showMarketplaceSuccess,
    marketplaceType,
    marketplaceTitle,
    marketplaceContent,
    marketplacePrice,
    marketplaceIsFreeGiveaway, // Issue #83
    marketplaceImages,
    marketplaceImagePreviews,
    isCreatingMarketplace,

    // Setters
    setMarketplaceType,
    setMarketplaceTitle,
    setMarketplaceContent,
    setMarketplacePrice,
    setMarketplaceIsFreeGiveaway, // Issue #83
    setMarketplaceImages,
    setMarketplaceImagePreviews,
    setIsCreatingMarketplace,

    // Actions
    closeMarketplaceModal,
    showSuccess,

    // Constants
    MAX_FILES,
    MAX_FILE_SIZE,
    ALLOWED_TYPES,
    MAX_IMAGE_DIMENSION,
    COMPRESSION_QUALITY
  } = useGlobalModal();

  // Image compression function
  const compressImage = (file) => {
    return new Promise((resolve, reject) => {
      if (file.size <= MAX_FILE_SIZE) {
        resolve(file);
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          let width = img.width;
          let height = img.height;

          if (width > MAX_IMAGE_DIMENSION || height > MAX_IMAGE_DIMENSION) {
            if (width > height) {
              height = (height / width) * MAX_IMAGE_DIMENSION;
              width = MAX_IMAGE_DIMENSION;
            } else {
              width = (width / height) * MAX_IMAGE_DIMENSION;
              height = MAX_IMAGE_DIMENSION;
            }
          }

          const canvas = document.createElement('canvas');
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);

          canvas.toBlob(
            (blob) => {
              if (blob) {
                const compressedFile = new File([blob], file.name, {
                  type: 'image/jpeg',
                  lastModified: Date.now(),
                });

                if (compressedFile.size > MAX_FILE_SIZE) {
                  reject(new Error(t('marketplace.imageTooLarge')));
                } else {
                  resolve(compressedFile);
                }
              } else {
                reject(new Error(t('marketplace.errorCompressing')));
              }
            },
            'image/jpeg',
            COMPRESSION_QUALITY
          );
        };
        img.onerror = () => reject(new Error(t('marketplace.errorLoading')));
        img.src = e.target.result;
      };
      reader.onerror = () => reject(new Error(t('marketplace.errorReading')));
      reader.readAsDataURL(file);
    });
  };

  // Handle image selection
  const handleImageSelect = async (e) => {
    const files = Array.from(e.target.files || []);

    if (marketplaceImages.length + files.length > MAX_FILES) {
      alert(t('marketplace.maxImagesError').replace('{count}', MAX_FILES));
      return;
    }

    const validFiles = [];
    const newPreviews = [];

    for (const file of files) {
      if (!ALLOWED_TYPES.includes(file.type)) {
        alert(t('marketplace.invalidFileType').replace('{name}', file.name));
        continue;
      }

      try {
        const compressedFile = await compressImage(file);
        validFiles.push(compressedFile);

        await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = (event) => {
            newPreviews.push(event.target.result);
            resolve();
          };
          reader.onerror = reject;
          reader.readAsDataURL(compressedFile);
        });
      } catch (err) {
        console.error('Compression failed:', err);
        validFiles.push(file);
        try {
          await new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = (event) => {
              newPreviews.push(event.target.result);
              resolve();
            };
            reader.onerror = reject;
            reader.readAsDataURL(file);
          });
        } catch (readerErr) {
          console.error('Preview failed:', readerErr);
        }
      }
    }

    setMarketplaceImages([...marketplaceImages, ...validFiles]);
    setMarketplaceImagePreviews([...marketplaceImagePreviews, ...newPreviews]);
  };

  // Remove image at index
  const removeImage = (index) => {
    setMarketplaceImages(marketplaceImages.filter((_, i) => i !== index));
    setMarketplaceImagePreviews(marketplaceImagePreviews.filter((_, i) => i !== index));
  };

  // Submit handler
  const handleSubmit = async () => {
    // Validation
    if (!marketplaceTitle.trim()) {
      alert(t('marketplace.pleaseEnterTitle'));
      return;
    }

    if (!marketplaceContent.trim()) {
      alert(t('marketplace.pleaseEnterDescription'));
      return;
    }

    if (marketplaceType === 'offer') {
      // Issue #83: Skip price validation if free giveaway
      if (!marketplaceIsFreeGiveaway && (!marketplacePrice || parseFloat(marketplacePrice) <= 0)) {
        alert(t('marketplace.pleaseEnterValidPrice'));
        return;
      }
      if (marketplaceImages.length === 0) {
        alert(t('marketplace.pleaseUploadImage'));
        return;
      }
    }

    setIsCreatingMarketplace(true);

    try {
      const formData = new FormData();
      formData.append('post_type', 'marketplace');
      formData.append('marketplace_type', marketplaceType);
      formData.append('title', marketplaceTitle);
      formData.append('content', marketplaceContent);

      // Issue #83: Add is_free_giveaway flag (only for offers)
      if (marketplaceType === 'offer') {
        formData.append('is_free_giveaway', marketplaceIsFreeGiveaway ? 'true' : 'false');
      }

      // Only add price if not a free giveaway
      if (marketplacePrice && !marketplaceIsFreeGiveaway) {
        formData.append('price', marketplacePrice);
      }

      marketplaceImages.forEach((file) => {
        formData.append('images', file);
      });

      const response = await axios.post('/api/posts', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (response.status === 200) {
        showSuccess();
      }
    } catch (error) {
      console.error('Error creating marketplace object:', error);
      alert(error.response?.data?.message || t('marketplace.errorCreating'));
    } finally {
      setIsCreatingMarketplace(false);
    }
  };

  // Render success overlay
  if (showMarketplaceSuccess) {
    return (
      <div className="modal-overlay" style={{ background: 'var(--overlay-color)' }}>
        <div className="modal-content" style={{ maxWidth: '400px', textAlign: 'center' }}>
          <div style={{ fontSize: '48px', color: 'var(--success-color, #2E7D32)', marginBottom: '12px' }}>&#10003;</div>
          <p style={{ marginBottom: '0', fontFamily: 'Poppins, sans-serif', fontSize: '16px' }}>
            {t('marketplace.objectCreatedSuccess')}
          </p>
        </div>
      </div>
    );
  }

  // Don't render if modal is not open
  if (!showMarketplaceModal) {
    return null;
  }

  // Main modal JSX
  return (
    <div className="modal-overlay" onClick={closeMarketplaceModal}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
        <h2 style={{ fontFamily: 'Poppins, sans-serif', fontSize: '20px', fontWeight: '600', marginBottom: '20px' }}>
          {t('marketplace.createObjectTitle')}
        </h2>

        {/* Type toggle */}
        <div style={{ marginBottom: '20px', display: 'flex', justifyContent: 'center' }}>
          <div style={{ display: 'flex', border: '1px solid var(--text-primary)', borderRadius: '100px', overflow: 'hidden' }}>
            <button
              onClick={() => setMarketplaceType('offer')}
              style={{
                fontFamily: 'Poppins, sans-serif',
                fontSize: '14px',
                fontWeight: '600',
                padding: '10px 24px',
                border: 'none',
                background: marketplaceType === 'offer' ? 'var(--bg-secondary)' : 'var(--bg-primary)',
                color: 'var(--text-primary)',
                cursor: 'pointer',
                borderRadius: '100px 0 0 100px',
                transition: 'background 0.2s'
              }}
            >
              {t('tabs.iOffer')}
            </button>
            <button
              onClick={() => setMarketplaceType('search')}
              style={{
                fontFamily: 'Poppins, sans-serif',
                fontSize: '14px',
                fontWeight: '600',
                padding: '10px 24px',
                border: 'none',
                background: marketplaceType === 'search' ? 'var(--bg-secondary)' : 'var(--bg-primary)',
                color: 'var(--text-primary)',
                cursor: 'pointer',
                borderRadius: '0 100px 100px 0',
                transition: 'background 0.2s'
              }}
            >
              {t('tabs.iSearch')}
            </button>
          </div>
        </div>

        {/* Image upload area */}
        <div style={{ marginBottom: '20px' }}>
          <input
            type="file"
            accept="image/*"
            multiple
            onChange={handleImageSelect}
            style={{ display: 'none' }}
            id="global-marketplace-image-upload"
          />
          <div
            onClick={() => document.getElementById('global-marketplace-image-upload').click()}
            style={{
              width: '100%',
              minHeight: '200px',
              background: marketplaceImagePreviews.length > 0
                ? 'transparent'
                : 'url(data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDgiIGhlaWdodD0iNDgiIHZpZXdCb3g9IjAgMCA0OCA0OCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHBhdGggZD0iTTI0IDEyVjM2TTEyIDI0SDM2IiBzdHJva2U9IiM5OTk5OTkiIHN0cm9rZS13aWR0aD0iMyIgc3Ryb2tlLWxpbmVjYXA9InJvdW5kIi8+Cjwvc3ZnPgo=) center/48px no-repeat, #ECE6F0',
              borderRadius: '28px',
              cursor: 'pointer',
              border: '2px dashed #D0D0D0',
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'flex-start',
              alignContent: 'flex-start',
              padding: '12px',
              gap: '8px',
              overflow: 'auto'
            }}
          >
            {marketplaceImagePreviews.length === 0 && (
              <div style={{
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'var(--text-secondary)',
                fontSize: '14px',
                fontFamily: 'Poppins, sans-serif',
                pointerEvents: 'none'
              }}>
                {marketplaceType === 'offer' ? t('marketplace.uploadImagesRequired') : t('marketplace.uploadImagesOptional')}
              </div>
            )}

            {marketplaceImagePreviews.map((preview, index) => (
              <div key={index} style={{ position: 'relative', width: '80px', height: '80px' }}>
                <img
                  src={preview}
                  alt={`Preview ${index + 1}`}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    borderRadius: '8px',
                    pointerEvents: 'none'
                  }}
                />
                <button
                  onClick={(e) => { e.stopPropagation(); removeImage(index); }}
                  style={{
                    position: 'absolute',
                    top: '-8px',
                    right: '-8px',
                    background: '#FF3B30',
                    color: 'white',
                    border: 'none',
                    borderRadius: '50%',
                    width: '24px',
                    height: '24px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 'bold'
                  }}
                >
                  x
                </button>
              </div>
            ))}
          </div>
          <p style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '8px', fontFamily: 'Poppins, sans-serif' }}>
            {t('marketplace.maxImagesInfo').replace('{count}', MAX_FILES).replace('{size}', MAX_FILE_SIZE / (1024 * 1024))}
          </p>
        </div>

        {/* Title field */}
        <div style={{ marginBottom: '16px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontSize: '15px', fontWeight: '600', fontFamily: 'Poppins, sans-serif' }}>
            {t('marketplace.titleLabel')}
          </label>
          <input
            type="text"
            value={marketplaceTitle}
            onChange={(e) => setMarketplaceTitle(e.target.value)}
            placeholder={marketplaceType === 'offer' ? t('marketplace.placeholderTitleOffer') : t('marketplace.placeholderTitleSearch')}
            style={{ width: '100%', padding: '12px', border: '1px solid #E0E0E0', borderRadius: '8px', fontSize: '14px', fontFamily: 'Poppins, sans-serif' }}
          />
        </div>

        {/* Description field */}
        <div style={{ marginBottom: '16px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontSize: '15px', fontWeight: '600', fontFamily: 'Poppins, sans-serif' }}>
            {t('marketplace.descriptionLabel')}
          </label>
          <textarea
            value={marketplaceContent}
            onChange={(e) => {
              setMarketplaceContent(e.target.value);
              e.target.style.height = 'auto';
              e.target.style.height = e.target.scrollHeight + 'px';
            }}
            placeholder={marketplaceType === 'offer' ? t('marketplace.placeholderDescriptionOffer') : t('marketplace.placeholderDescriptionSearch')}
            style={{ width: '100%', padding: '12px', border: '1px solid #E0E0E0', borderRadius: '8px', fontSize: '14px', overflow: 'hidden', resize: 'none', fontFamily: 'Poppins, sans-serif', minHeight: '80px', height: 'auto', boxSizing: 'border-box' }}
          />
        </div>

        {/* Issue #83: Free giveaway checkbox (only for offers) */}
        {marketplaceType === 'offer' && (
          <div style={{ marginBottom: '16px' }}>
            <label style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              cursor: 'pointer',
              fontFamily: 'Poppins, sans-serif',
              fontSize: '12px',
              fontWeight: '400',
              lineHeight: '140%',
              color: 'var(--text-primary)',
              width: 'fit-content',
              minWidth: '120px',
              height: '17px'
            }}>
              <input
                type="checkbox"
                checked={marketplaceIsFreeGiveaway}
                onChange={(e) => setMarketplaceIsFreeGiveaway(e.target.checked)}
                style={{ display: 'none' }}
              />
              <span style={{
                width: '16px',
                height: '16px',
                minWidth: '16px',
                background: marketplaceIsFreeGiveaway ? '#2C2C2C' : '#FFFFFF',
                border: marketplaceIsFreeGiveaway ? 'none' : '1px solid #2C2C2C',
                borderRadius: '4px',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                cursor: 'pointer'
              }}>
                {marketplaceIsFreeGiveaway && (
                  <svg width="10" height="8" viewBox="0 0 10 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M1 4L3.5 6.5L9 1" stroke="#F5F5F5" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                )}
              </span>
              <span>{t('marketplace.freeGiveaway')}</span>
            </label>
          </div>
        )}

        {/* Price field (only for offers) */}
        {marketplaceType === 'offer' && (
          <div style={{
            marginBottom: '16px',
            opacity: marketplaceIsFreeGiveaway ? 0.2 : 1,
            transition: 'opacity 0.2s'
          }}>
            <label style={{ display: 'block', marginBottom: '8px', fontSize: '15px', fontWeight: '600', fontFamily: 'Poppins, sans-serif' }}>
              {t('marketplace.priceLabel')}
            </label>
            <input
              type="number"
              value={marketplacePrice}
              onChange={(e) => setMarketplacePrice(e.target.value)}
              placeholder="0.00"
              step="0.01"
              min="0"
              disabled={marketplaceIsFreeGiveaway}
              style={{
                width: '156px',
                padding: '8px 16px',
                background: 'rgba(37, 41, 126, 0.09)',
                border: '1px solid #E0E0E0',
                borderRadius: '16px',
                fontSize: '14px',
                fontFamily: 'Poppins, sans-serif',
                cursor: marketplaceIsFreeGiveaway ? 'not-allowed' : 'text'
              }}
            />
            <span style={{ marginLeft: '8px', fontSize: '14px', fontFamily: 'Poppins, sans-serif' }}>EUR</span>
          </div>
        )}

        {/* Buttons */}
        <div style={{ ...popoverButtonContainerStyle, marginTop: '24px' }}>
          <button
            onClick={closeMarketplaceModal}
            disabled={isCreatingMarketplace}
            style={getCancelButtonStyle(isCreatingMarketplace)}
          >
            {t('cancel')}
          </button>
          <button
            onClick={handleSubmit}
            disabled={isCreatingMarketplace}
            style={getSubmitButtonStyle(isCreatingMarketplace)}
          >
            {isCreatingMarketplace ? t('marketplace.creating') : t('marketplace.create')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default MarketplaceModal;
